/**
 * Label node editor — static heading with optional link. Bubble menu has
 * text input + url input + heading-size preset (h1..h6). Preview renders
 * the text at the preset's font size.
 */

import { ArrowDown, ArrowUp, Trash2, Type } from 'lucide-react';
import type { LabelNode, LabelPreset } from '@news-cms/shared';
import { LABEL_PRESETS } from '@news-cms/shared';
import { useEditor } from './editor-context';
import { NodeToolbar } from './node-toolbar';

/** Tailwind text size + weight per preset — mirrors how the public site
 *  is expected to render h1..h6. Kept inline to avoid a separate map file. */
const PRESET_CLASS: Record<LabelPreset, string> = {
  h1: 'text-3xl font-bold',
  h2: 'text-2xl font-semibold',
  h3: 'text-xl font-semibold',
  h4: 'text-lg font-medium',
  h5: 'text-base font-medium',
  h6: 'text-sm font-medium',
};

export function LabelNodeEditor({ node }: { node: LabelNode }) {
  const { selectedId, select, updateLabel, move, remove } = useEditor();
  const isSelected = selectedId === node.id;
  const className = PRESET_CLASS[node.preset];

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        select(node.id);
      }}
      className={`relative rounded border px-px pb-0.5 pt-2 transition ${
        isSelected
          ? 'border-sky-400 bg-sky-50/40 dark:border-sky-500 dark:bg-sky-950/20'
          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
      }`}
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
        <Type className="h-3 w-3" />
        Label · {node.preset}
        {node.url && (
          <span className="rounded bg-sky-100 px-1.5 text-[9px] text-sky-600 dark:bg-sky-900/50 dark:text-sky-300">
            link
          </span>
        )}
      </div>

      <NodeToolbar
        visible={isSelected}
        extra={
          <div className="mr-1 flex items-center gap-1 border-r border-slate-200 pr-2 dark:border-slate-700">
            <input
              type="text"
              value={node.text}
              onChange={(e) => updateLabel(node.id, { text: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              placeholder="ข้อความ"
              className="border-input bg-background h-7 w-[200px] rounded border px-1.5 text-xs"
            />
            <input
              type="text"
              value={node.url ?? ''}
              onChange={(e) => updateLabel(node.id, { url: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              placeholder="URL (optional)"
              title="ใส่ URL เพื่อให้ label เป็นลิงก์ (เว้นว่างได้)"
              className="border-input bg-background h-7 w-[180px] rounded border px-1.5 text-xs"
            />
            <select
              value={node.preset}
              onChange={(e) => updateLabel(node.id, { preset: e.target.value as LabelPreset })}
              onClick={(e) => e.stopPropagation()}
              title="ขนาดหัวข้อ"
              className="border-input bg-background h-7 w-[60px] rounded border px-1.5 text-xs"
            >
              {LABEL_PRESETS.map((p) => (
                <option key={p} value={p}>
                  {p.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        }
        buttons={[
          {
            key: 'up',
            label: 'เลื่อนขึ้น',
            icon: <ArrowUp className="h-3.5 w-3.5" />,
            onClick: () => move(node.id, 'up'),
          },
          {
            key: 'down',
            label: 'เลื่อนลง',
            icon: <ArrowDown className="h-3.5 w-3.5" />,
            onClick: () => move(node.id, 'down'),
          },
          {
            key: 'delete',
            label: 'ลบ',
            icon: <Trash2 className="h-3.5 w-3.5" />,
            onClick: () => remove(node.id),
            variant: 'destructive',
          },
        ]}
      />

      <div className="px-2 py-1">
        {node.url ? (
          <a
            href={node.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={`${className} text-sky-700 hover:underline dark:text-sky-300`}
          >
            {node.text || '(ว่าง)'}
          </a>
        ) : (
          <span className={`${className} text-slate-800 dark:text-slate-100`}>
            {node.text || '(ว่าง)'}
          </span>
        )}
      </div>
    </div>
  );
}
